import { getClaims, type useAuth } from "@workos-inc/authkit-react";
import { authRedirectState } from "@/lib/auth-return-to";
import { captureUserActionError } from "@/lib/sentry";

type OrganizationAuth = Pick<ReturnType<typeof useAuth>, "switchToOrganization" | "getAccessToken">;

/**
 * Moves the session into a company the server has already reconciled, then opens `destination`.
 * When AuthKit cannot refresh into the organization it starts hosted sign-in instead; that
 * redirect carries the destination, so this must not navigate over it.
 */
export async function enterOrganization(
  auth: OrganizationAuth,
  organizationId: string,
  destination: string
): Promise<"navigated" | "reauthenticating"> {
  try {
    await auth.switchToOrganization({
      organizationId,
      signInOpts: { state: authRedirectState(destination) },
    });
  } catch (error) {
    // Membership is saved server-side; the app resolves it on load even without the org claim.
    captureUserActionError(error, "auth.switchToOrganization");
    window.location.assign(destination);
    return "navigated";
  }

  try {
    if (getClaims(await auth.getAccessToken()).org_id !== organizationId) {
      return "reauthenticating";
    }
  } catch {
    return "reauthenticating";
  }
  window.location.assign(destination);
  return "navigated";
}
